import { LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form } from 'antd'
import { useNavigate } from 'react-router-dom'

import {
  useStocksServiceCreateConsumableEndpoint,
  useStocksServiceGetConsumableListEndpointKey
} from 'src/api/queries'
import { ApiError } from 'src/api/requests'
import { FormFooter, message, PageBreadcrumbs } from 'src/components'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

import { ConsumableFields } from '../components'

export const CreateConsumablePage = () => {
  const { t } = useLocales()
  const [form] = Form.useForm()

  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const { mutate, isPending } = useStocksServiceCreateConsumableEndpoint({
    onError: (e: ApiError) => form.setFields(formErrorHandler(e.body.errors)),
    onSuccess: () => {
      message.success(t('common.save_success'))
      queryClient.invalidateQueries({
        queryKey: [useStocksServiceGetConsumableListEndpointKey]
      })
      navigate(paths.medicinesRegister.consumables.root)
    }
  })

  const onCancel = () => {
    form.resetFields()
    navigate(paths.medicinesRegister.consumables.root)
  }

  const onSubmit = async () => {
    const requestBody = await form.validateFields()

    mutate({
      requestBody: {
        commercialName: requestBody.commercialName,
        formId: requestBody.formId,
        internationalNameId: requestBody.internationalNameId,
        producerId: requestBody.producerId,
        measurementUnitId: requestBody.measurementUnitId,
        groupId: requestBody.groupId.value
      }
    })
  }

  return (
    <PageBreadcrumbs
      items={[
        {
          title: t('menu.medicines_register')
        },
        {
          title: t('medicine_register.consumables.title')
        },
        {
          title: t('medicine_register.consumables.add')
        }
      ]}
      title={t('medicine_register.consumables.add')}
    >
      <Flex vertical gap={24}>
        <Flex justify="space-between" gap={16}>
          <Button icon={<LeftOutlined />} onClick={onCancel}>
            {t('common.back')}
          </Button>
        </Flex>

        <Form form={form} layout="vertical" requiredMark={false}>
          <ConsumableFields />

          <FormFooter
            onCancel={onCancel}
            onSave={onSubmit}
            saveConfirmTitle={`${t('common.add')}?`}
            saveLabel={t('common.add')}
            loading={isPending}
          />
        </Form>
      </Flex>
    </PageBreadcrumbs>
  )
}
